import { useEffect, useRef, useState } from "react";

import {
  GLOBAL_SEARCH_TYPES,
  searchGlobalContent,
} from "../services/globalSearchService";

const LIMIT = 12;

const initialState = {
  results: [],
  total: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: "",
};

export function useGlobalSearch(type = "videos", search = "") {
  const [state, setState] = useState(initialState);

  const requestId = useRef(0);
  const loadingMoreRef = useRef(false);

  const query = String(search || "").trim();
  const validType = GLOBAL_SEARCH_TYPES.includes(type);

  useEffect(() => {
    const id = ++requestId.current;
    loadingMoreRef.current = false;

    if (!query || !validType) {
      setState(initialState);
      return;
    }

    setState({ ...initialState, loading: true });

    const fetchResults = async () => {
      try {
        const response = await searchGlobalContent(type, query, LIMIT, 0);

        if (id !== requestId.current) return;

        const data = response.data || {};
        const results = Array.isArray(data.results) ? data.results : [];

        const total = Number(data.total) || 0;

        setState({
          ...initialState,
          results,
          total,
          hasMore: results.length < total,
        });
      } catch (error) {
        if (id !== requestId.current) return;

        setState({
          ...initialState,
          error: error.response?.data?.message || "Unable to search content.",
        });
      }
    };

    fetchResults();

    return () => {
      requestId.current++;
    };
  }, [type, query, validType]);

  const loadMore = async () => {
    if (
      !query ||
      !validType ||
      state.loading ||
      state.loadingMore ||
      !state.hasMore ||
      loadingMoreRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;

    const id = requestId.current;
    const offset = state.results.length;

    setState((previous) => ({
      ...previous,
      loadingMore: true,
      error: "",
    }));

    try {
      const response = await searchGlobalContent(type, query, LIMIT, offset);

      if (id !== requestId.current) return;

      const data = response.data || {};
      const newResults = Array.isArray(data.results) ? data.results : [];

      const total = Number(data.total) || 0;

      setState((previous) => {
        const results = [...previous.results, ...newResults];

        return {
          ...previous,
          results,
          total,
          hasMore: newResults.length > 0 && results.length < total,
          loadingMore: false,
        };
      });
    } catch (error) {
      if (id !== requestId.current) return;

      setState((previous) => ({
        ...previous,
        loadingMore: false,
        error: error.response?.data?.message || "Unable to load more results.",
      }));
    } finally {
      if (id === requestId.current) {
        loadingMoreRef.current = false;
      }
    }
  };

  return {
    ...state,
    hasSearch: Boolean(query && validType),
    validType,
    loadMore,
  };
}
